import { POSTAL_ADDRESS_COLUMNS, type Company } from "@somasi/shared";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/integrations/aws/resources";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { CountryField } from "@/components/country-field";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { RowAction, RowActions } from "@/components/row-actions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Building2, Download, Pencil, Plus, Receipt, Trash2 } from "lucide-react";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import { exportCSV, exportXLSX, exportPDF } from "@/lib/export-utils";
import { ImportButton } from "@/components/import-dialog";
import { Truncated } from "@/components/truncated";
import {
  CLIENT_GROUP_CONTACT_COLUMNS,
  CLIENT_GROUP_PDF_COLUMNS,
  CLIENT_GROUP_TEMPLATE_COLUMNS,
  GROUP_COMPANY_PDF_COLUMNS,
  TYPES,
  TYPE_LABEL,
  clientGroupExportRow,
  groupCompanyExportRow,
  groupFilenameBase,
  validateClientGroupRows,
  type ExportableContact,
} from "@/lib/client-groups";
import { z } from "zod";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
} from "@/components/list-controls";

export const Route = createFileRoute("/_authenticated/client-groups")({
  head: () => ({
    meta: [
      { title: "Client Groups — Somasi Ops" },
      { name: "description", content: "Master list of Somasi client groups." },
    ],
  }),
  component: ClientGroupsPage,
});

export type ClientGroup = {
  id: string;
  name: string;
  client_type: "direct" | "b2b_corporate" | "intermediary" | "individual";
  country: string | null;
  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  default_payment_terms: string | null;
  is_active: boolean;
  notes: string | null;
  updated_at: string;
};

/**
 * Re-exported for the pages that already read the type vocabulary from here —
 * the client group page among them. It lives in `@/lib/client-groups`, with the
 * import and export column contract that has to agree with it.
 */
export { TYPES, TYPE_LABEL };

/** An address part: optional, trimmed, and short enough to print on a line. */
const addressPart = (max: number) => z.string().trim().max(max).optional().or(z.literal(""));

const schema = z.object({
  name: z.string().trim().min(1, "Name required").max(200),
  client_type: z.enum(["direct", "b2b_corporate", "intermediary", "individual"]),
  country: z.string().trim().max(100).optional().or(z.literal("")),
  address_line1: addressPart(200),
  address_line2: addressPart(200),
  address_city: addressPart(100),
  address_state: addressPart(100),
  address_postal_code: addressPart(30),
  default_payment_terms: z.string().trim().max(50).optional().or(z.literal("")),
  is_active: z.boolean(),
  notes: z.string().trim().max(2000).optional().or(z.literal("")),
});

function ClientGroupsPage() {
  const { canEdit, canAdmin } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClientGroup | null>(null);
  /** The group whose companies are being fetched, so its menu can say so. */
  const [downloading, setDownloading] = useState<string | null>(null);

  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());
  const sort = useListSort({ column: "name", ascending: true });
  const pager = usePagination({ resetKey: { query, sort: sort.sort } });

  /**
   * The list the search describes — the table pages it, the export takes it whole.
   *
   * The search used to run in the browser over a full download of the table, and
   * it matched the *label* of the type as well as the name and the country. The
   * label is not a column, so matching it server-side means resolving the typed
   * text back to the type values it names and asking for those: "corp" is
   * `client_type in (b2b_corporate)`, as one more alternative in the same OR
   * group. Nothing about the search changes for the person using it; what
   * changes is that the browser no longer has to hold every group to run it.
   */
  const filteredList = () => {
    const list = api.clientGroups
      .list()
      .order(sort.sort.column, { ascending: sort.sort.ascending });
    // Ties — every active group, say — fall back to the name, so a page break
    // never lands somewhere different between two requests.
    if (sort.sort.column !== "name") list.order("name");
    if (!query) return list;

    const needle = query.toLowerCase();
    const typesMatched = TYPES.filter((t) => t.label.toLowerCase().includes(needle)).map(
      (t) => t.value,
    );

    list.or([
      { column: "name", op: "ilike", value: `%${query}%` },
      { column: "country", op: "ilike", value: `%${query}%` },
      ...(typesMatched.length
        ? [{ column: "client_type", op: "in" as const, value: typesMatched }]
        : []),
    ]);
    return list;
  };

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "client_groups",
      { query, sort: sort.sort, page: pager.page, pageSize: pager.pageSize },
    ],
    queryFn: async () => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as ClientGroup[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const filtered = result?.rows ?? [];
  const total = result?.total;

  const sel = useRowSelection(filtered);

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row: ClientGroup) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const remove = async (row: ClientGroup) => {
    if (
      !(await confirmDelete(
        `“${row.name}”`,
        "Companies linked to it keep their records but lose the group link.",
      ))
    ) {
      return;
    }
    const { error } = await api.clientGroups.remove(row.id);
    if (error) return notify.error(error);
    notify.success("Client group deleted");
    qc.invalidateQueries({ queryKey: ["client_groups"] });
  };

  /**
   * The groups the search describes, each with the people attached to it.
   *
   * The contacts are one read for the whole file rather than one per group: a
   * group's contacts sit in its own index partition, but asking per group would
   * be a request per row, and naming every group's id in one `in` filter would
   * put hundreds of UUIDs in a query string. So the group-level contacts — the
   * ones with no `company_id`, which is how the model holds "a contact of this
   * client rather than of one of its companies" — are read once and bucketed
   * here by the group they belong to.
   */
  const exportRows = async () => {
    const [groups, contacts] = await Promise.all([
      filteredList().all(),
      api.contacts
        .list()
        .all()
        .select(CLIENT_GROUP_CONTACT_COLUMNS)
        .is("company_id", null)
        .order("full_name"),
    ]);
    if (groups.error) throw new Error(groups.error.message);
    if (contacts.error) throw new Error(contacts.error.message);

    const byGroup = new Map<string, ExportableContact[]>();
    for (const contact of (contacts.data ?? []) as (ExportableContact & {
      client_group_id: string | null;
    })[]) {
      if (!contact.client_group_id) continue;
      const bucket = byGroup.get(contact.client_group_id);
      if (bucket) bucket.push(contact);
      else byGroup.set(contact.client_group_id, [contact]);
    }

    return ((groups.data ?? []) as ClientGroup[]).map((group) =>
      clientGroupExportRow(group, byGroup.get(group.id) ?? []),
    );
  };

  /**
   * An import file's rows, checked against what is already stored.
   *
   * The names are read for the duplicate warning only — this importer inserts,
   * so a file naming a group that exists makes a second one, and the operator
   * should see that before committing rather than after.
   */
  const validateImport = async (parsed: Parameters<typeof validateClientGroupRows>[0]) => {
    const { data, error } = await api.clientGroups.list().all().select("id, name");
    if (error) throw new Error(error.message);
    return validateClientGroupRows(
      parsed,
      ((data ?? []) as { name: string }[]).map((g) => g.name),
    );
  };

  /**
   * Every company under one group, as a file of its own.
   *
   * Not a filter of the companies export: that one is reached from the
   * companies list and writes the group as a column, where this is "send me
   * what you hold on this client" and is reached from the client. The read is a
   * Query on `by_client_group_id`, so a group with 655 companies costs one
   * round trip rather than a table scan.
   */
  const downloadCompanies = async (group: ClientGroup, format: "xlsx" | "csv" | "pdf") => {
    setDownloading(group.id);
    try {
      const { data, error } = await api.companies
        .list()
        .eq("client_group_id", group.id)
        .order("legal_name")
        .all();
      if (error) throw new Error(error.message);

      const rows = ((data ?? []) as Company[]).map(groupCompanyExportRow);
      if (rows.length === 0) {
        return notify.error(`“${group.name}” has no companies to download yet`);
      }

      const filename = groupFilenameBase(group.name, "companies");
      if (format === "xlsx") exportXLSX(filename, rows, "Companies");
      else if (format === "csv") exportCSV(filename, rows);
      else {
        exportPDF({
          filename,
          title: `${group.name} — Companies`,
          subtitle: `${rows.length} ${rows.length === 1 ? "company" : "companies"} under this client group`,
          columns: GROUP_COMPANY_PDF_COLUMNS,
          rows,
        });
      }
      notify.success(
        `Downloaded ${rows.length} ${rows.length === 1 ? "company" : "companies"} for “${group.name}”`,
      );
    } catch (error) {
      notify.error(error);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Client Groups"
          description="Companies billed together for one client. Open a group to see its companies and contacts."
          actions={
            <>
              <ExportMenu
                filenameBase="somasi-client-groups"
                rows={exportRows}
                pdfCols={CLIENT_GROUP_PDF_COLUMNS}
                title="Client Groups"
              />
              {/*
                Admin-only because `POST /import/commit` is: the importer writes
                rows straight past the resource handler, so the button matches
                the permission rather than offering one the server will refuse.
              */}
              {canAdmin && (
                <ImportButton
                  table="client_groups"
                  entityLabel="client group"
                  entityLabelPlural="client groups"
                  template={{
                    filename: "somasi-client-groups-template.xlsx",
                    columns: CLIENT_GROUP_TEMPLATE_COLUMNS,
                    sheetName: "Client Groups",
                  }}
                  hint="Name is all a row needs. The columns are the export's, minus the contacts — those are edited on the contacts page, so they are read-only here."
                  validate={validateImport}
                  onImported={() => qc.invalidateQueries({ queryKey: ["client_groups"] })}
                />
              )}
              {canEdit && (
                <Button onClick={openNew}>
                  <Plus className="mr-2 h-4 w-4" /> New group
                </Button>
              )}
            </>
          }
        />

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search by name, country or type…"
          />
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.clientGroups}
            entityLabel="client group"
            activeColumn="is_active"
            onDone={() => qc.invalidateQueries({ queryKey: ["client_groups"] })}
          />
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="name">
                Name
              </SortHeader>
              <SortHeader sort={sort} column="client_type">
                Type
              </SortHeader>
              <SortHeader sort={sort} column="country">
                Country
              </SortHeader>
              <SortHeader sort={sort} column="default_payment_terms">
                Payment terms
              </SortHeader>
              <SortHeader sort={sort} column="is_active">
                Status
              </SortHeader>
              <TableHead className="w-px whitespace-nowrap text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={canEdit ? 7 : 6}>Loading…</TableMessageRow>
            ) : filtered.length === 0 ? (
              <TableMessageRow colSpan={canEdit ? 7 : 6}>
                {query ? "No client groups match that search." : "No client groups yet."}
              </TableMessageRow>
            ) : (
              filtered.map((row) => (
                <TableRow key={row.id} data-state={sel.isSelected(row.id) ? "selected" : undefined}>
                  {canEdit && (
                    <TableCell>
                      <RowCheckbox sel={sel} id={row.id} />
                    </TableCell>
                  )}
                  <TableCell className="max-w-xs">
                    <Link
                      to="/client-groups/$groupId"
                      params={{ groupId: row.id }}
                      className="block font-medium hover:underline"
                    >
                      <Truncated text={row.name} />
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{TYPE_LABEL[row.client_type]}</Badge>
                  </TableCell>
                  <TableCell>{row.country ?? "—"}</TableCell>
                  <TableCell>{row.default_payment_terms ?? "—"}</TableCell>
                  <TableCell>
                    {row.is_active ? (
                      <Badge className="bg-success text-success-foreground hover:bg-success/90">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {/*
                      The menu is no longer gated on `canEdit` as a whole: the
                      companies download under it is a read, and a read-only
                      operator asked to send a client's company list is exactly
                      the person who needs it. The writes inside it are still
                      gated one by one.
                    */}
                    <RowActions>
                      {canEdit && (
                        <RowAction label="Edit" onClick={() => openEdit(row)}>
                          <Pencil />
                        </RowAction>
                      )}
                      <RowAction label="Companies & contacts" asChild>
                        <Link to="/client-groups/$groupId" params={{ groupId: row.id }}>
                          <Building2 />
                        </Link>
                      </RowAction>
                      <RowAction label="Group pricing" asChild>
                        <Link to="/client-groups/$groupId/pricing" params={{ groupId: row.id }}>
                          <Receipt />
                        </Link>
                      </RowAction>
                      {/* Three formats, so the one icon still opens a short menu. */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <RowAction
                            label={downloading === row.id ? "Preparing…" : "Download companies"}
                            disabled={downloading === row.id}
                          >
                            <Download />
                          </RowAction>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => downloadCompanies(row, "xlsx")}>
                            Excel (.xlsx)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadCompanies(row, "csv")}>
                            CSV
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadCompanies(row, "pdf")}>
                            PDF report
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {canEdit && (
                        <RowAction label="Delete" destructive onClick={() => remove(row)}>
                          <Trash2 />
                        </RowAction>
                      )}
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="client group" />
      </ListPageTable>

      <ClientGroupDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={() => qc.invalidateQueries({ queryKey: ["client_groups"] })}
      />
    </ListPage>
  );
}

export function ClientGroupDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: ClientGroup | null;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<z.infer<typeof schema>>({
    name: "",
    client_type: "direct",
    country: "",
    address_line1: "",
    address_line2: "",
    address_city: "",
    address_state: "",
    address_postal_code: "",
    default_payment_terms: "Due on receipt",
    is_active: true,
    notes: "",
  });

  // Reset when opened.
  //
  // This was a `useMemo`, which calls `setForm` during render. React treats a
  // memo as a cache it may discard and recompute at will, so every time it did,
  // the form was reset mid-edit — and a country added from the picker was
  // selected and then immediately wiped back to the editing row's value. The
  // other dialogs already use an effect; this one now matches.
  useEffect(() => {
    if (open) {
      setForm({
        name: editing?.name ?? "",
        client_type: editing?.client_type ?? "direct",
        country: editing?.country ?? "",
        address_line1: editing?.address_line1 ?? "",
        address_line2: editing?.address_line2 ?? "",
        address_city: editing?.address_city ?? "",
        address_state: editing?.address_state ?? "",
        address_postal_code: editing?.address_postal_code ?? "",
        default_payment_terms: editing?.default_payment_terms ?? "Due on receipt",
        is_active: editing?.is_active ?? true,
        notes: editing?.notes ?? "",
      });
    }
  }, [open, editing]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) return notify.error(parsed.error.issues[0].message);
    setSaving(true);
    const payload = {
      ...parsed.data,
      country: parsed.data.country || null,
      // An address part left blank is "not set", not an empty line to print.
      ...(Object.fromEntries(
        POSTAL_ADDRESS_COLUMNS.map((column) => [column, parsed.data[column] || null]),
      ) as Record<(typeof POSTAL_ADDRESS_COLUMNS)[number], string | null>),
      default_payment_terms: parsed.data.default_payment_terms || null,
      notes: parsed.data.notes || null,
    };
    const { error } = editing
      ? await api.clientGroups.update(editing.id, payload)
      : await api.clientGroups.create(payload);
    setSaving(false);
    if (error) return notify.error(error);
    notify.success(editing ? "Client group updated" : "Client group created");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit client group" : "New client group"}</DialogTitle>
          <DialogDescription>
            Keep it simple — you can always fill in details later.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label required>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={form.client_type}
                  onValueChange={(v) =>
                    setForm({ ...form, client_type: v as ClientGroup["client_type"] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <CountryField
                value={form.country ?? ""}
                onChange={(country) => setForm({ ...form, country })}
              />
            </div>

            {/*
              The billing address, part by part. It sits under the country rather
              than above it because the country field is also the address's last
              line — the one column answers both, so it is asked once.
            */}
            <div className="space-y-3 rounded-md border p-3">
              <div>
                <Label className="text-sm font-medium">Billing address</Label>
                <p className="text-xs text-muted-foreground">
                  Where an invoice to this group is addressed. The country is the one chosen above.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address_line1">Address line 1</Label>
                <Input
                  id="address_line1"
                  placeholder="1001 Brickell Bay Drive"
                  value={form.address_line1}
                  onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address_line2">Address line 2</Label>
                <Input
                  id="address_line2"
                  placeholder="Suite 3200"
                  value={form.address_line2}
                  onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="address_city">City</Label>
                  <Input
                    id="address_city"
                    placeholder="Miami"
                    value={form.address_city}
                    onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_state">State / province</Label>
                  <Input
                    id="address_state"
                    placeholder="Florida"
                    value={form.address_state}
                    onChange={(e) => setForm({ ...form, address_state: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="address_postal_code">Postal code</Label>
                  <Input
                    id="address_postal_code"
                    placeholder="33131"
                    value={form.address_postal_code}
                    onChange={(e) => setForm({ ...form, address_postal_code: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Payment terms</Label>
                <Input
                  value={form.default_payment_terms}
                  onChange={(e) => setForm({ ...form, default_payment_terms: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label>Active</Label>
                <p className="text-xs text-muted-foreground">
                  Inactive groups stay in history but are hidden from most workflows.
                </p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export type ExportRows = Record<string, unknown>[];

/**
 * Export of a list, in three formats.
 *
 * `rows` may be a function, and on a paged list it has to be. The page holds
 * fifty rows out of however many match, so exporting what the page has would
 * hand someone a file that silently stops at the bottom of the screen — and the
 * ones most likely to export are the ones with the most rows. A function is
 * called when the menu item is clicked and fetches the whole filtered set, so
 * the file is the list the filters describe rather than the slice of it that
 * happened to be on screen.
 */
export function ExportMenu({
  filenameBase,
  rows,
  pdfCols,
  title,
}: {
  filenameBase: string;
  rows: ExportRows | (() => Promise<ExportRows>);
  pdfCols: { header: string; dataKey: string }[];
  title: string;
}) {
  const [busy, setBusy] = useState(false);

  const run = async (write: (resolved: ExportRows) => void) => {
    if (typeof rows !== "function") return write(rows);
    setBusy(true);
    try {
      write(await rows());
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={busy}>
          <Download className="mr-2 h-4 w-4" /> {busy ? "Exporting…" : "Export"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => run((r) => exportXLSX(filenameBase, r))}>
          Export as Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => run((r) => exportCSV(filenameBase, r))}>
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() =>
            run((r) => exportPDF({ filename: filenameBase, title, columns: pdfCols, rows: r }))
          }
        >
          Export as PDF report
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
